// API Client utility for making requests to the backend server

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  message?: string;
}

class ApiClient {
  private baseURL: string;
  private getToken?: () => Promise<string | null>;

  constructor(baseURL: string = API_BASE_URL, tokenProvider?: () => Promise<string | null>) {
    this.baseURL = baseURL;
    this.getToken = tokenProvider;
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}${endpoint}`;
      
      // Get the authentication token if available
      const token = this.getToken ? await this.getToken() : null;
      
      const config: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options.headers,
        },
        ...options,
      };

      console.log(`API Request: ${config.method || 'GET'} ${url}`);

      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} ${response.statusText}`, errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          return { error: errorJson.message || errorJson.error || `HTTP ${response.status}` };
        } catch {
          return { error: `HTTP ${response.status}: ${response.statusText}` };
        }
      }

      const data = response.status === 204 ? undefined : await response.json();
      return { data };
    } catch (error) {
      console.error('API Request failed:', error);
      return { 
        error: error instanceof Error ? error.message : 'Network request failed' 
      };
    }
  }

  // GET request
  async get<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST request
  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // POST request for file downloads (returns blob)
  async postForFile(endpoint: string, data?: any): Promise<{ blob?: Blob; error?: string }> {
    try {
      const url = `${this.baseURL}${endpoint}`;
      
      // Get the authentication token if available
      const token = this.getToken ? await this.getToken() : null;
      
      const config: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: data ? JSON.stringify(data) : undefined,
      };

      console.log(`API File Request: POST ${url}`);

      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} ${response.statusText}`, errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          return { error: errorJson.message || errorJson.error || `HTTP ${response.status}` };
        } catch {
          return { error: `HTTP ${response.status}: ${response.statusText}` };
        }
      }

      const blob = await response.blob();
      return { blob };
    } catch (error) {
      console.error('API File Request failed:', error);
      return { 
        error: error instanceof Error ? error.message : 'Network request failed' 
      };
    }
  }

  // PUT request
  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Get the base URL for debugging
  getBaseURL(): string {
    return this.baseURL;
  }
}

// Create a singleton instance
const apiClient = new ApiClient();

// Export the instance and class
export default apiClient;
export { ApiClient, API_BASE_URL };

// Export commonly used API endpoints
export const API_ENDPOINTS = {
  HEALTH: '/api/health',
  TEAMS: '/api/teams',
  BOARDS: '/api/boards',
  TEMPLATES: '/api/templates',
  EXPORT: '/api/export',
} as const;